/*
 * Minimal QR Code encoder (byte mode, versions 1-15) rendered as SVG.
 * Used to draw otpauth:// URIs returned by the 2FA setup endpoint.
 */

const EXP_TABLE = new Array(256)
const LOG_TABLE = new Array(256)

for (let i = 0; i < 8; i++) {
  EXP_TABLE[i] = 1 << i
}
for (let i = 8; i < 256; i++) {
  EXP_TABLE[i] = EXP_TABLE[i - 4] ^ EXP_TABLE[i - 5] ^ EXP_TABLE[i - 6] ^ EXP_TABLE[i - 8]
}
for (let i = 0; i < 255; i++) {
  LOG_TABLE[EXP_TABLE[i]] = i
}

const G15 = (1 << 10) | (1 << 8) | (1 << 5) | (1 << 4) | (1 << 2) | (1 << 1) | (1 << 0)
const G18 = (1 << 12) | (1 << 11) | (1 << 10) | (1 << 9) | (1 << 8) | (1 << 5) | (1 << 2) | (1 << 0)
const G15_MASK = (1 << 14) | (1 << 12) | (1 << 10) | (1 << 4) | (1 << 1)

const PAD0 = 0xEC
const PAD1 = 0x11

// numeric identifiers used inside the format information
const EC_LEVEL_BITS = { L: 1, M: 0, Q: 3, H: 2 }
// offset of each level inside RS_BLOCK_TABLE
const EC_LEVEL_ORDER = { L: 0, M: 1, Q: 2, H: 3 }

// [blocks, total codewords, data codewords, ...] per version, ordered L, M, Q, H
const RS_BLOCK_TABLE = [
  [1, 26, 19], [1, 26, 16], [1, 26, 13], [1, 26, 9],
  [1, 44, 34], [1, 44, 28], [1, 44, 22], [1, 44, 16],
  [1, 70, 55], [1, 70, 44], [2, 35, 17], [2, 35, 13],
  [1, 100, 80], [2, 50, 32], [2, 50, 24], [4, 25, 9],
  [1, 134, 108], [2, 67, 43], [2, 33, 15, 2, 34, 16], [2, 33, 11, 2, 34, 12],
  [2, 86, 68], [4, 43, 27], [4, 43, 19], [4, 43, 15],
  [2, 98, 78], [4, 49, 31], [2, 32, 14, 4, 33, 15], [4, 39, 13, 1, 40, 14],
  [2, 121, 97], [2, 60, 38, 2, 61, 39], [4, 40, 18, 2, 41, 19], [4, 40, 14, 2, 41, 15],
  [2, 146, 116], [3, 58, 36, 2, 59, 37], [4, 36, 16, 4, 37, 17], [4, 36, 12, 4, 37, 13],
  [2, 86, 68, 2, 87, 69], [4, 69, 43, 1, 70, 44], [6, 43, 19, 2, 44, 20], [6, 43, 15, 2, 44, 16],
  [4, 101, 81], [1, 80, 50, 4, 81, 51], [4, 50, 22, 4, 51, 23], [3, 36, 12, 8, 37, 13],
  [2, 116, 92, 2, 117, 93], [6, 58, 36, 2, 59, 37], [4, 46, 20, 6, 47, 21], [7, 42, 14, 4, 43, 15],
  [4, 133, 107], [8, 59, 37, 1, 60, 38], [8, 44, 20, 4, 45, 21], [12, 33, 11, 4, 34, 12],
  [3, 145, 115, 1, 146, 116], [4, 64, 40, 5, 65, 41], [11, 36, 16, 5, 37, 17], [11, 36, 12, 5, 37, 13],
  [5, 109, 87, 1, 110, 88], [5, 65, 41, 5, 66, 42], [5, 54, 24, 7, 55, 25], [11, 36, 12, 7, 37, 13]
]

const ALIGNMENT_PATTERNS = [
  [], [6, 18], [6, 22], [6, 26], [6, 30],
  [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50],
  [6, 30, 54], [6, 32, 58], [6, 34, 62], [6, 26, 46, 66], [6, 26, 48, 70]
]

const MAX_VERSION = 15

function gexp (n) {
  while (n < 0) n += 255
  while (n >= 256) n -= 255
  return EXP_TABLE[n]
}

function glog (n) {
  return LOG_TABLE[n]
}

function trimPolynomial (num, shift) {
  let offset = 0
  while (offset < num.length && num[offset] === 0) offset++
  const result = []
  for (let i = 0; i < num.length - offset; i++) {
    result.push(num[i + offset])
  }
  for (let i = 0; i < shift; i++) {
    result.push(0)
  }
  return result
}

function multiplyPolynomials (a, b) {
  const result = new Array(a.length + b.length - 1).fill(0)
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      result[i + j] ^= gexp(glog(a[i]) + glog(b[j]))
    }
  }
  return trimPolynomial(result, 0)
}

function modPolynomials (a, b) {
  if (a.length - b.length < 0) return a
  const ratio = glog(a[0]) - glog(b[0])
  const result = a.slice()
  for (let i = 0; i < b.length; i++) {
    result[i] ^= gexp(glog(b[i]) + ratio)
  }
  return modPolynomials(trimPolynomial(result, 0), b)
}

function errorCorrectionPolynomial (length) {
  let poly = [1]
  for (let i = 0; i < length; i++) {
    poly = multiplyPolynomials(poly, [1, gexp(i)])
  }
  return poly
}

function bchDigit (data) {
  let digit = 0
  while (data !== 0) {
    digit++
    data >>>= 1
  }
  return digit
}

function bchTypeInfo (data) {
  let d = data << 10
  while (bchDigit(d) - bchDigit(G15) >= 0) {
    d ^= (G15 << (bchDigit(d) - bchDigit(G15)))
  }
  return ((data << 10) | d) ^ G15_MASK
}

function bchTypeNumber (data) {
  let d = data << 12
  while (bchDigit(d) - bchDigit(G18) >= 0) {
    d ^= (G18 << (bchDigit(d) - bchDigit(G18)))
  }
  return (data << 12) | d
}

function maskAt (pattern, i, j) {
  switch (pattern) {
    case 0: return (i + j) % 2 === 0
    case 1: return i % 2 === 0
    case 2: return j % 3 === 0
    case 3: return (i + j) % 3 === 0
    case 4: return (Math.floor(i / 2) + Math.floor(j / 3)) % 2 === 0
    case 5: return ((i * j) % 2) + ((i * j) % 3) === 0
    case 6: return (((i * j) % 2) + ((i * j) % 3)) % 2 === 0
    case 7: return (((i * j) % 3) + ((i + j) % 2)) % 2 === 0
    default: return false
  }
}

function rsBlocks (version, level) {
  const entry = RS_BLOCK_TABLE[(version - 1) * 4 + EC_LEVEL_ORDER[level]]
  const blocks = []
  for (let i = 0; i < entry.length; i += 3) {
    const count = entry[i]
    const totalCount = entry[i + 1]
    const dataCount = entry[i + 2]
    for (let j = 0; j < count; j++) {
      blocks.push({ totalCount, dataCount })
    }
  }
  return blocks
}

function dataCapacity (version, level) {
  return rsBlocks(version, level).reduce((total, block) => total + block.dataCount, 0)
}

function charCountBits (version) {
  return version < 10 ? 8 : 16
}

function toUtf8Bytes (text) {
  const bytes = []
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i)
    if (code < 0x80) {
      bytes.push(code)
    } else if (code < 0x800) {
      bytes.push(0xC0 | (code >> 6), 0x80 | (code & 0x3F))
    } else if (code < 0xD800 || code >= 0xE000) {
      bytes.push(0xE0 | (code >> 12), 0x80 | ((code >> 6) & 0x3F), 0x80 | (code & 0x3F))
    } else {
      i++
      const point = 0x10000 + (((code & 0x3FF) << 10) | (text.charCodeAt(i) & 0x3FF))
      bytes.push(
        0xF0 | (point >> 18),
        0x80 | ((point >> 12) & 0x3F),
        0x80 | ((point >> 6) & 0x3F),
        0x80 | (point & 0x3F)
      )
    }
  }
  return bytes
}

function createBitBuffer () {
  return {
    buffer: [],
    length: 0,
    putBit (bit) {
      const index = Math.floor(this.length / 8)
      if (this.buffer.length <= index) this.buffer.push(0)
      if (bit) this.buffer[index] |= (0x80 >>> (this.length % 8))
      this.length++
    },
    put (num, length) {
      for (let i = 0; i < length; i++) {
        this.putBit(((num >>> (length - i - 1)) & 1) === 1)
      }
    }
  }
}

function pickVersion (byteLength, level) {
  for (let version = 1; version <= MAX_VERSION; version++) {
    const capacityBits = dataCapacity(version, level) * 8
    const requiredBits = 4 + charCountBits(version) + byteLength * 8
    if (requiredBits <= capacityBits) return version
  }
  throw new Error('QR content is too long')
}

function createCodewords (bytes, version, level) {
  const blocks = rsBlocks(version, level)
  const buffer = createBitBuffer()

  buffer.put(4, 4)
  buffer.put(bytes.length, charCountBits(version))
  bytes.forEach((byte) => buffer.put(byte, 8))

  const totalDataBits = blocks.reduce((total, block) => total + block.dataCount, 0) * 8
  if (buffer.length + 4 <= totalDataBits) {
    buffer.put(0, 4)
  }
  while (buffer.length % 8 !== 0) {
    buffer.putBit(false)
  }
  let padIndex = 0
  while (buffer.buffer.length * 8 < totalDataBits) {
    buffer.buffer.push(padIndex % 2 === 0 ? PAD0 : PAD1)
    padIndex++
  }

  const dataBlocks = []
  const ecBlocks = []
  let offset = 0
  let maxDataLength = 0
  let maxEcLength = 0

  blocks.forEach((block) => {
    const ecLength = block.totalCount - block.dataCount
    const data = buffer.buffer.slice(offset, offset + block.dataCount)
    offset += block.dataCount

    const generator = errorCorrectionPolynomial(ecLength)
    const remainder = modPolynomials(trimPolynomial(data, generator.length - 1), generator)
    const ec = []
    for (let i = 0; i < generator.length - 1; i++) {
      const index = i + remainder.length - (generator.length - 1)
      ec.push(index >= 0 ? remainder[index] : 0)
    }

    dataBlocks.push(data)
    ecBlocks.push(ec)
    maxDataLength = Math.max(maxDataLength, data.length)
    maxEcLength = Math.max(maxEcLength, ec.length)
  })

  const codewords = []
  for (let i = 0; i < maxDataLength; i++) {
    dataBlocks.forEach((block) => {
      if (i < block.length) codewords.push(block[i])
    })
  }
  for (let i = 0; i < maxEcLength; i++) {
    ecBlocks.forEach((block) => {
      if (i < block.length) codewords.push(block[i])
    })
  }
  return codewords
}

function createEmptyModules (size) {
  const modules = new Array(size)
  for (let row = 0; row < size; row++) {
    modules[row] = new Array(size).fill(null)
  }
  return modules
}

function setupFinderPattern (modules, size, offsetRow, offsetCol) {
  for (let r = -1; r <= 7; r++) {
    if (offsetRow + r < 0 || size <= offsetRow + r) continue
    for (let c = -1; c <= 7; c++) {
      if (offsetCol + c < 0 || size <= offsetCol + c) continue
      const isBorder = (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
        (c >= 0 && c <= 6 && (r === 0 || r === 6))
      const isCenter = r >= 2 && r <= 4 && c >= 2 && c <= 4
      modules[offsetRow + r][offsetCol + c] = isBorder || isCenter
    }
  }
}

function setupTimingPattern (modules, size) {
  for (let i = 8; i < size - 8; i++) {
    if (modules[i][6] === null) modules[i][6] = i % 2 === 0
    if (modules[6][i] === null) modules[6][i] = i % 2 === 0
  }
}

function setupAlignmentPatterns (modules, version) {
  const positions = ALIGNMENT_PATTERNS[version - 1]
  positions.forEach((row) => {
    positions.forEach((col) => {
      if (modules[row][col] !== null) return
      for (let r = -2; r <= 2; r++) {
        for (let c = -2; c <= 2; c++) {
          modules[row + r][col + c] = r === -2 || r === 2 || c === -2 || c === 2 ||
            (r === 0 && c === 0)
        }
      }
    })
  })
}

function setupVersionInfo (modules, size, version) {
  if (version < 7) return
  const bits = bchTypeNumber(version)
  for (let i = 0; i < 18; i++) {
    const dark = ((bits >> i) & 1) === 1
    modules[Math.floor(i / 3)][i % 3 + size - 8 - 3] = dark
    modules[i % 3 + size - 8 - 3][Math.floor(i / 3)] = dark
  }
}

function setupFormatInfo (modules, size, level, maskPattern) {
  const bits = bchTypeInfo((EC_LEVEL_BITS[level] << 3) | maskPattern)

  for (let i = 0; i < 15; i++) {
    const dark = ((bits >> i) & 1) === 1
    if (i < 6) modules[i][8] = dark
    else if (i < 8) modules[i + 1][8] = dark
    else modules[size - 15 + i][8] = dark
  }

  for (let i = 0; i < 15; i++) {
    const dark = ((bits >> i) & 1) === 1
    if (i < 8) modules[8][size - i - 1] = dark
    else if (i < 9) modules[8][15 - i - 1 + 1] = dark
    else modules[8][15 - i - 1] = dark
  }

  modules[size - 8][8] = true
}

function placeCodewords (modules, size, codewords, maskPattern) {
  let inc = -1
  let row = size - 1
  let bitIndex = 7
  let byteIndex = 0

  for (let col = size - 1; col > 0; col -= 2) {
    if (col === 6) col--
    for (;;) {
      for (let c = 0; c < 2; c++) {
        if (modules[row][col - c] !== null) continue
        let dark = false
        if (byteIndex < codewords.length) {
          dark = ((codewords[byteIndex] >>> bitIndex) & 1) === 1
        }
        if (maskAt(maskPattern, row, col - c)) dark = !dark
        modules[row][col - c] = dark
        bitIndex--
        if (bitIndex === -1) {
          byteIndex++
          bitIndex = 7
        }
      }
      row += inc
      if (row < 0 || size <= row) {
        row -= inc
        inc = -inc
        break
      }
    }
  }
}

function buildMatrix (codewords, version, level, maskPattern) {
  const size = version * 4 + 17
  const modules = createEmptyModules(size)

  setupFinderPattern(modules, size, 0, 0)
  setupFinderPattern(modules, size, size - 7, 0)
  setupFinderPattern(modules, size, 0, size - 7)
  setupAlignmentPatterns(modules, version)
  setupTimingPattern(modules, size)
  setupVersionInfo(modules, size, version)
  setupFormatInfo(modules, size, level, maskPattern)
  placeCodewords(modules, size, codewords, maskPattern)

  return { size, modules }
}

// ISO/IEC 18004 penalty rules, used to choose the mask pattern
function penalty (modules, size) {
  let lost = 0

  // rule 1: runs of five or more modules of the same colour
  const scoreRun = (run) => (run >= 5 ? 3 + (run - 5) : 0)
  for (let i = 0; i < size; i++) {
    let rowRun = 1
    let colRun = 1
    for (let j = 1; j < size; j++) {
      rowRun = modules[i][j] === modules[i][j - 1] ? rowRun + 1 : (lost += scoreRun(rowRun), 1)
      colRun = modules[j][i] === modules[j - 1][i] ? colRun + 1 : (lost += scoreRun(colRun), 1)
    }
    lost += scoreRun(rowRun)
    lost += scoreRun(colRun)
  }

  // rule 2: blocks of the same colour
  for (let row = 0; row < size - 1; row++) {
    for (let col = 0; col < size - 1; col++) {
      const first = modules[row][col]
      if (modules[row + 1][col] === first && modules[row][col + 1] === first &&
        modules[row + 1][col + 1] === first) {
        lost += 3
      }
    }
  }

  // rule 3: finder-like 1:1:3:1:1 pattern followed or preceded by four light modules
  const FINDER = [true, false, true, true, true, false, true]
  const matches = (get, start) => {
    for (let i = 0; i < FINDER.length; i++) {
      if (get(start + i) !== FINDER[i]) return false
    }
    let leading = true
    for (let i = start - 4; i < start; i++) {
      if (i < 0 || get(i)) {
        leading = false
        break
      }
    }
    if (leading) return true
    for (let i = start + FINDER.length; i < start + FINDER.length + 4; i++) {
      if (i >= size || get(i)) return false
    }
    return true
  }
  for (let i = 0; i < size; i++) {
    for (let start = 0; start <= size - FINDER.length; start++) {
      if (matches((index) => modules[i][index], start)) lost += 40
      if (matches((index) => modules[index][i], start)) lost += 40
    }
  }

  // rule 4: deviation from an even balance of dark and light modules
  let darkCount = 0
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (modules[row][col]) darkCount++
    }
  }
  const percent = (darkCount * 100) / (size * size)
  lost += Math.floor(Math.abs(percent - 50) / 5) * 10

  return lost
}

function encode (text, options) {
  const settings = options || {}
  const level = EC_LEVEL_BITS[settings.errorCorrectionLevel] === undefined
    ? 'M'
    : settings.errorCorrectionLevel
  const bytes = toUtf8Bytes(String(text))
  const version = settings.version || pickVersion(bytes.length, level)
  const codewords = createCodewords(bytes, version, level)

  if (settings.maskPattern !== undefined && settings.maskPattern !== null) {
    return buildMatrix(codewords, version, level, settings.maskPattern)
  }

  let best = null
  let bestScore = Infinity
  for (let maskPattern = 0; maskPattern < 8; maskPattern++) {
    const candidate = buildMatrix(codewords, version, level, maskPattern)
    const score = penalty(candidate.modules, candidate.size)
    if (score < bestScore) {
      bestScore = score
      best = candidate
    }
  }
  return best
}

function toSvg (text, options) {
  const settings = options || {}
  const { size, modules } = encode(text, settings)
  const margin = settings.margin === undefined ? 2 : settings.margin
  const dimension = size + margin * 2
  const width = settings.width || 220

  let path = ''
  for (let row = 0; row < size; row++) {
    for (let col = 0; col < size; col++) {
      if (modules[row][col]) {
        path += `M${col + margin} ${row + margin}h1v1h-1z`
      }
    }
  }

  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${width}" ` +
    `viewBox="0 0 ${dimension} ${dimension}" shape-rendering="crispEdges">` +
    `<rect width="${dimension}" height="${dimension}" fill="#ffffff"/>` +
    `<path fill="#000000" d="${path}"/></svg>`
}

function toDataUrl (text, options) {
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(toSvg(text, options))
}

const qr = { encode, toSvg, toDataUrl }

export default qr
